// src/pages/Todo/TodoCard.tsx — handles both Personal and Assigned items
import React from "react";
import { motion } from "framer-motion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { DateField } from "../../components/ui/DateField";
import {
  Check, Clock, AlertTriangle, Repeat, MoreHorizontal, GripVertical,
  Link2, User, Briefcase, Pin, X, Trash2, Pencil, PlayCircle, Paperclip,
} from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { format, parseISO, isPast, isToday } from "date-fns";
import { attachmentHref } from "../../api/todo";
import type { PlannerItem, PersonalTodo, AssignedItem, TodoUpdate, TodoAttachment } from "../../api/todo";
import {
  combineHoursAndMinutes,
  estimatedMinutesToHours,
  formatHours,
  hoursToEstimatedMinutes,
  splitHoursAndMinutes,
} from "../../utils/taskWorkloadCapacity";

const PRIORITY_STYLES: Record<string, { bar: string; pill: string }> = {
  URGENT: { bar: "is-urgent", pill: "gt-pill is-urgent" },
  HIGH:   { bar: "is-high",   pill: "gt-pill is-high" },
  MEDIUM: { bar: "is-medium", pill: "gt-pill is-medium" },
  LOW:    { bar: "is-low",    pill: "gt-pill is-low" },
};

const STATUS_PILLS: Record<string, string> = {
  PENDING:     "gt-pill is-pending",
  TODO:        "gt-pill is-pending",
  IN_PROGRESS: "gt-pill is-progress",
  COMPLETED:   "gt-pill is-done",
  DONE:        "gt-pill is-done",
  IN_REVIEW:   "gt-pill is-high",
  BLOCKED:     "gt-pill is-overdue",
  CANCELLED:   "gt-pill is-ghost",
  MISSED:      "gt-pill is-overdue",
};

const isPersonal = (it: PlannerItem): it is PersonalTodo => it.source === "personal";

interface Props {
  item: PlannerItem;
  onToggle?: (it: PersonalTodo) => void;
  onStatusChange?: (it: PersonalTodo, newStatus: string) => void;
  onOpen?: (it: PlannerItem) => void;
  onLink?: (it: AssignedItem) => void;
  onUnlink?: (it: AssignedItem) => void;
  onDelete?: (it: PersonalTodo) => void;
  onEdit?: (it: PersonalTodo, changes: TodoUpdate) => void;
  variant?: "today" | "upcoming" | "missed";
}

const PRIO_COLORS: Record<string, string> = {
  URGENT: '#dc2626',
  HIGH:   '#ea580c',
  MEDIUM: '#d97706',
  LOW:    '#2563eb',
};

export default function TodoCard({ item, onToggle, onStatusChange, onOpen, onLink, onUnlink, onDelete, onEdit, variant = "today" }: Props) {
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [editMode, setEditMode] = React.useState(false);
  const [editTitle, setEditTitle] = React.useState("");
  const [editDesc, setEditDesc] = React.useState("");
  const [editPriority, setEditPriority] = React.useState("");
  const [editDue, setEditDue] = React.useState("");
  const [editWorkloadHours, setEditWorkloadHours] = React.useState("");
  const [editWorkloadMinutes, setEditWorkloadMinutes] = React.useState("");
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);
  // Drag-drop only enabled for Personal todos (assigned tasks are read-only here).
  // Delegated items are the assigner's read-only copy — no dragging.
  const draggable = isPersonal(item) && (item as PersonalTodo).delegated !== true;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    data: { item },
    disabled: !draggable,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    minHeight: 0,
  };

  const priority = PRIORITY_STYLES[item.priority] || PRIORITY_STYLES.MEDIUM;
  const isDone = isPersonal(item) ? item.status === "COMPLETED" : item.status === "DONE";
  const isInProgress = isPersonal(item) && item.status === "IN_PROGRESS";
  const dueRaw = item.due_date;
  const due = dueRaw ? parseISO(dueRaw + (dueRaw.length === 10 ? "T00:00:00" : "")) : null;
  const overdue = !isPersonal(item)
    ? item.is_overdue
    : (due && !isDone && isPast(due) && !isToday(due));

  // Delegated read-only view: the assigner's copy of a task they delegated (now completed).
  const delegated = isPersonal(item) && (item as PersonalTodo).delegated === true;
  const attachments: TodoAttachment[] = (isPersonal(item) ? (item as PersonalTodo).attachments : undefined) || [];
  const estimatedMinutes = isPersonal(item) ? (item as PersonalTodo).estimated_minutes : undefined;
  const workloadLabel = estimatedMinutes !== null && estimatedMinutes !== undefined
    ? formatHours(estimatedMinutesToHours(estimatedMinutes))
    : null;

  // const handleCheckboxClick = (e: React.MouseEvent) => {
  //   e.stopPropagation();
  //   if (isPersonal(item) && onToggle) {
  //     // Directly mark as completed on checkbox click
  //     onToggle(item);
  //   }
  // };

  const handleCheckboxClick = (e) => {
  e.stopPropagation();
  if (isPersonal(item) && onStatusChange && item.status !== "COMPLETED") {
    onStatusChange(item, "COMPLETED");
  }
};

  const handleMarkInProgress = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isPersonal(item) && onStatusChange && item.status !== "IN_PROGRESS") {
      onStatusChange(item, "IN_PROGRESS");
    }
  };

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -10 }}
      transition={{ type: "spring", stiffness: 280, damping: 22 }}
      className={`group gt-task-card flex gap-2 cursor-pointer overflow-hidden is-prio-${(item.priority || 'medium').toLowerCase()} ${isDone ? "is-done" : ""} ${overdue ? "is-overdue" : ""}`}
      onClick={() => onOpen?.(item)}
    >
      <div className={`gt-prio-bar ${priority.bar}`} style={{ position: 'relative', borderRadius: 0 }} />

      {draggable ? (
        <button
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          className="self-stretch px-1 cursor-grab active:cursor-grabbing"
          style={{ color: 'var(--gt-text-tertiary)' }}
          aria-label="Drag"
        >
          <GripVertical className="w-4 h-4" />
        </button>
      ) : (
        <div className="self-stretch px-1" style={{ color: 'var(--gt-text-disabled)' }}>
          <Link2 className="w-4 h-4 mt-3" />
        </div>
      )}

      <div className="flex-1 min-w-0 py-3 pr-3">
        {editMode && isPersonal(item) ? (
          <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
            <input
              autoFocus
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className="gt-input"
              style={{ padding: '8px 12px', fontSize: 13 }}
              placeholder="Title"
            />
            <textarea
              value={editDesc}
              onChange={(e) => setEditDesc(e.target.value)}
              rows={2}
              className="gt-textarea"
              style={{ padding: '8px 12px', fontSize: 12, resize: 'none' }}
              placeholder="Description (optional)"
            />
            <div className="flex gap-2">
              <div style={{ flex: 1 }}>
                <Select value={editPriority} onValueChange={setEditPriority}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["LOW", "MEDIUM", "HIGH", "URGENT"].map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div style={{ flex: 1 }}>
                <DateField
                  value={editDue}
                  onChange={setEditDue}
                  size="sm"
                  placeholder="Due date"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <input
                aria-label="Workload hours"
                type="number"
                min="0"
                step="1"
                value={editWorkloadHours}
                onChange={(e) => setEditWorkloadHours(e.target.value)}
                placeholder="Hours"
                className="gt-input"
                style={{ flex: 1, padding: '8px 12px', fontSize: 12 }}
              />
              <input
                aria-label="Workload minutes"
                type="number"
                min="0"
                max="59"
                step="15"
                value={editWorkloadMinutes}
                onChange={(e) => setEditWorkloadMinutes(e.target.value)}
                placeholder="Min"
                className="gt-input"
                style={{ flex: 1, padding: '8px 12px', fontSize: 12 }}
              />
            </div>
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => {
                  const nextWorkload = hoursToEstimatedMinutes(
                    combineHoursAndMinutes(editWorkloadHours, editWorkloadMinutes),
                  );
                  const changes: TodoUpdate = {};
                  if (editTitle !== item.title) changes.title = editTitle || undefined;
                  if (editDesc !== (item.description || "")) changes.description = editDesc || undefined;
                  if (editPriority !== item.priority) changes.priority = editPriority as any;
                  if (editDue !== (item.due_date || "")) changes.due_date = editDue || undefined;
                  if (nextWorkload !== ((item as PersonalTodo).estimated_minutes ?? undefined)) {
                    changes.estimated_minutes = nextWorkload ?? null;
                  }
                  onEdit?.(item, changes);
                  setEditMode(false);
                }}
                className="gt-btn is-primary is-sm"
                style={{ flex: 1, justifyContent: 'center' }}
              >
                Save
              </button>
              <button
                onClick={() => setEditMode(false)}
                className="gt-btn is-ghost is-sm"
                style={{ flex: 1, justifyContent: 'center' }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-2.5">
            {isPersonal(item) && !delegated ? (
              <button
                onClick={handleCheckboxClick}
                className="mt-0.5 shrink-0 flex items-center justify-center"
                style={{
                  width: 20, height: 20,
                  borderRadius: 999,
                  border: `2px solid ${isDone ? 'var(--gt-accent-strong)' : isInProgress ? '#3b82f6' : 'var(--gt-border-strong)'}`,
                  background: isDone ? 'var(--gt-accent-strong)' : isInProgress ? '#dbeafe' : 'transparent',
                  color: isDone ? '#fff' : isInProgress ? '#1e40af' : 'var(--gt-primary)',
                  transition: 'all 200ms cubic-bezier(0.33, 1, 0.68, 1)',
                }}
                aria-label={isDone ? "Mark not done" : "Mark done"}
              >
                {isDone && <Check className="w-3 h-3" strokeWidth={3} />}
                {isInProgress && !isDone && <PlayCircle className="w-3 h-3" strokeWidth={2} />}
              </button>
            ) : (
              <div className="mt-0.5 shrink-0 flex items-center justify-center"
                style={{
                  width: 20, height: 20, borderRadius: 999,
                  background: isDone ? 'var(--gt-accent-strong)' : 'var(--gt-surface-2)',
                  color: isDone ? '#fff' : 'var(--gt-text-tertiary)',
                }}
                title={delegated ? "Read-only (delegated task)" : "Read-only (assigned task)"}>
                {isDone ? <Check className="w-3 h-3" strokeWidth={3} /> : <Briefcase className="w-3 h-3" />}
              </div>
            )}

            <div className="flex-1 min-w-0">
              <h3 className="gt-display line-clamp-2"
                style={{
                  fontSize: 13.5, fontWeight: 600, lineHeight: 1.35,
                  color: isDone ? 'var(--gt-text-disabled)' : 'var(--gt-text-primary)',
                  textDecoration: isDone ? 'line-through' : 'none',
                }}
              >
                {item.title}
              </h3>
              {item.description && (
                <p className="line-clamp-2" style={{
                  fontSize: 12,
                  color: isDone ? 'var(--gt-text-disabled)' : 'var(--gt-text-tertiary)',
                  marginTop: 4,
                }}>{item.description}</p>
              )}

              {isDone ? (
                <>
                  <div style={{
                    display: 'flex', alignItems: 'center', flexWrap: 'wrap',
                    gap: '6px 10px', marginTop: 8, fontSize: 11.5,
                    color: 'var(--gt-text-tertiary)', fontWeight: 500,
                  }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {isPersonal(item) ? <User className="w-3 h-3" /> : <Briefcase className="w-3 h-3" />}
                      {isPersonal(item) ? 'Personal' : 'Assigned'}
                    </span>
                    <span style={{ color: 'var(--gt-border-strong)' }}>·</span>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      color: PRIO_COLORS[item.priority] || PRIO_COLORS.MEDIUM,
                      fontWeight: 700, letterSpacing: '0.02em',
                    }}>
                      <span style={{
                        width: 7, height: 7, borderRadius: '50%',
                        background: 'currentColor', flexShrink: 0,
                      }} />
                      {item.priority}
                    </span>
                    {workloadLabel && (
                      <>
                        <span style={{ color: 'var(--gt-border-strong)' }}>·</span>
                        <span className="gt-numerals" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Clock className="w-3 h-3" />
                          {workloadLabel}
                        </span>
                      </>
                    )}
                    {due && (
                      <>
                        <span style={{ color: 'var(--gt-border-strong)' }}>·</span>
                        <span className="gt-numerals" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Clock className="w-3 h-3" />
                          Due {format(due, "MMM d, yyyy")}
                        </span>
                      </>
                    )}
                    {isPersonal(item) && (item as any).completed_at && (
                      <>
                        <span style={{ color: 'var(--gt-border-strong)' }}>·</span>
                        <span className="gt-numerals" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#16a34a', fontWeight: 700 }}>
                          <Check className="w-3 h-3" />
                          Done {format(new Date((item as any).completed_at), "MMM d, p")}
                        </span>
                      </>
                    )}
                    {isPersonal(item) && (item as any).source_task_id && (
                      <>
                        <span style={{ color: 'var(--gt-border-strong)' }}>·</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#9a3412', fontWeight: 600 }} title="From a task checklist">
                          <Briefcase className="w-3 h-3" /> From Task
                        </span>
                      </>
                    )}
                    {isPersonal(item) && item.is_recurring && (
                      <>
                        <span style={{ color: 'var(--gt-border-strong)' }}>·</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Repeat className="w-3 h-3" /> recurring
                        </span>
                      </>
                    )}
                  </div>
                  {attachments.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2.5" onClick={(e) => e.stopPropagation()}>
                      {attachments.map((att, i) => (
                        <a
                          key={`${att.url}-${i}`}
                          href={attachmentHref(att.url)}
                          target="_blank"
                          rel="noreferrer"
                          download={att.name}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1.5 max-w-[180px] px-2 py-1 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700 text-[11px] font-medium transition-colors"
                          title={att.name}
                        >
                          <Paperclip className="w-3 h-3 shrink-0" />
                          <span className="truncate">{att.name}</span>
                        </a>
                      ))}
                    </div>
                  )}
                  {delegated ? (
                    <div className="mt-2.5" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#16a34a', fontWeight: 700 }}>
                      <Check className="w-3.5 h-3.5" /> Completed by {(item as PersonalTodo).owner_name || "assignee"}
                    </div>
                  ) : (
                    <div className="mt-2.5" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#16a34a', fontWeight: 700 }}>
                      <Check className="w-3.5 h-3.5" /> Completed
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Inline metadata row — not buttons, just informational text */}
                  <div style={{
                    display: 'flex', alignItems: 'center', flexWrap: 'wrap',
                    gap: '6px 10px', marginTop: 8, fontSize: 11.5,
                    color: 'var(--gt-text-tertiary)', fontWeight: 500,
                  }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {isPersonal(item) ? <User className="w-3 h-3" /> : <Briefcase className="w-3 h-3" />}
                      {isPersonal(item) ? 'Personal' : 'Assigned'}
                    </span>
                    <span style={{ color: 'var(--gt-border-strong)' }}>·</span>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      color: PRIO_COLORS[item.priority] || PRIO_COLORS.MEDIUM,
                      fontWeight: 700, letterSpacing: '0.02em',
                    }}>
                      <span style={{
                        width: 7, height: 7, borderRadius: '50%',
                        background: 'currentColor', flexShrink: 0,
                      }} />
                      {item.priority}
                    </span>
                    {workloadLabel && (
                      <>
                        <span style={{ color: 'var(--gt-border-strong)' }}>·</span>
                        <span className="gt-numerals" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Clock className="w-3 h-3" />
                          {workloadLabel}
                        </span>
                      </>
                    )}
                    <span style={{ color: 'var(--gt-border-strong)' }}>·</span>
                    <span style={{ textTransform: 'capitalize' }}>
                      {item.status.replace(/_/g, " ").toLowerCase()}
                    </span>
                    {due && (
                      <>
                        <span style={{ color: 'var(--gt-border-strong)' }}>·</span>
                        <span className="gt-numerals" style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          color: overdue ? '#b91c1c' : 'var(--gt-text-tertiary)',
                        }}>
                          {overdue ? <AlertTriangle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                          {format(due, variant === "today" ? "p" : "MMM d")}
                        </span>
                      </>
                    )}
                    {isPersonal(item) && (item as any).source_task_id && (
                      <>
                        <span style={{ color: 'var(--gt-border-strong)' }}>·</span>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          color: '#9a3412', fontWeight: 600,
                        }} title="Created from a task checklist">
                          <Briefcase className="w-3 h-3" /> From Task
                        </span>
                      </>
                    )}
                    {isPersonal(item) && item.is_recurring && (
                      <>
                        <span style={{ color: 'var(--gt-border-strong)' }}>·</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Repeat className="w-3 h-3" /> recurring
                        </span>
                      </>
                    )}
                  </div>

                  {attachments.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2.5" onClick={(e) => e.stopPropagation()}>
                      {attachments.map((att, i) => (
                        <a
                          key={`${att.url}-${i}`}
                          href={attachmentHref(att.url)}
                          target="_blank"
                          rel="noreferrer"
                          download={att.name}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1.5 max-w-[180px] px-2 py-1 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700 text-[11px] font-medium transition-colors"
                          title={att.name}
                        >
                          <Paperclip className="w-3 h-3 shrink-0" />
                          <span className="truncate">{att.name}</span>
                        </a>
                      ))}
                    </div>
                  )}

                  {isPersonal(item) && !delegated && (
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      {!isInProgress && onStatusChange ? (
                        <button
                          onClick={handleMarkInProgress}
                          className="gt-btn"
                          style={{
                            justifyContent: 'center', width: '100%', padding: '9px 14px',
                            background: '#dbeafe', color: '#1e40af', border: '1px solid transparent', fontWeight: 600,
                          }}
                        >
                          <PlayCircle className="w-4 h-4" /> In Progress
                        </button>
                      ) : (
                        <button
                          disabled
                          className="gt-btn"
                          style={{
                            justifyContent: 'center', width: '100%', padding: '9px 14px',
                            background: '#dbeafe', color: '#1e40af', border: '1px solid transparent', fontWeight: 600, opacity: 1,
                          }}
                        >
                          <PlayCircle className="w-4 h-4" /> In Progress
                        </button>
                      )}
                      {onStatusChange && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onStatusChange?.(item, "COMPLETED");
                          }}
                          className="gt-btn"
                          style={{
                            justifyContent: 'center', width: '100%', padding: '9px 14px',
                            background: '#16a34a', color: '#ffffff', border: '1px solid transparent', fontWeight: 700,
                            boxShadow: '0 1px 2px rgba(22, 163, 74, 0.25)',
                          }}
                        >
                          <Check className="w-4 h-4" /> Complete
                        </button>
                      )}
                    </div>
                  )}

                  {!isPersonal(item) && (item.progress_percent ?? 0) > 0 && item.progress_percent !== 100 && (
                    <div className="gt-progress-track mt-2" style={{ height: 4 }}>
                      <motion.div
                        className="gt-progress-fill"
                        initial={{ width: 0 }}
                        animate={{ width: `${item.progress_percent}%` }}
                        transition={{ duration: 0.4 }}
                      />
                    </div>
                  )}
                </>
              )}
            </div>

            {!isPersonal(item) && variant === "today" && (
              item.link_id ? (
                <button
                  onClick={(e) => { e.stopPropagation(); onUnlink?.(item); }}
                  className="gt-icon-btn is-sm opacity-0 group-hover:opacity-100"
                  style={{ color: 'var(--gt-dnd)' }}
                  aria-label="Unpin"
                  title="Unpin from today"
                >
                  <X className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={(e) => { e.stopPropagation(); onLink?.(item); }}
                  className="gt-icon-btn is-sm opacity-0 group-hover:opacity-100"
                  aria-label="Pin to today"
                  title="Pin to today"
                >
                  <Pin className="w-4 h-4" />
                </button>
              )
            )}

            {isPersonal(item) && !delegated && (onEdit || onDelete) && (
              confirmDelete ? (
                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => { onDelete?.(item); setConfirmDelete(false); }}
                    className="gt-btn is-danger is-sm"
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => setConfirmDelete(false)}
                    className="gt-btn is-ghost is-sm"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div ref={menuRef} style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={(e) => { e.stopPropagation(); setMenuOpen(v => !v); }}
                    className="gt-icon-btn is-sm"
                    aria-label="More"
                    title="More actions"
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                  {menuOpen && (
                    <div
                      className="gt-dropdown"
                      style={{
                        position: 'absolute', right: 0, top: '110%',
                        minWidth: 140, padding: '6px', zIndex: 20,
                      }}
                    >
                      {onEdit && (
                        <button
                          onClick={() => {
                            setMenuOpen(false);
                            setEditTitle(item.title);
                            setEditDesc(item.description || "");
                            setEditPriority(item.priority);
                            setEditDue(item.due_date || "");
                            const parts = splitHoursAndMinutes(estimatedMinutesToHours((item as PersonalTodo).estimated_minutes));
                            setEditWorkloadHours(parts.hours);
                            setEditWorkloadMinutes(parts.minutes);
                            setEditMode(true);
                          }}
                          className="gt-dropdown-item"
                          style={{ borderRadius: 8 }}
                        >
                          <Pencil className="w-4 h-4" /> Edit
                        </button>
                      )}
                      {onDelete && (
                        <button
                          onClick={() => { setMenuOpen(false); setConfirmDelete(true); }}
                          className="gt-dropdown-item"
                          style={{ color: 'var(--gt-dnd)', borderRadius: 8 }}
                        >
                          <Trash2 className="w-4 h-4" /> Delete
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}
